import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { NavEntry } from "@solomon-apps/shared";
import {
    BullhornIcon,
    CoinIcon,
    GraphIcon,
    LayoutGridIcon,
    MoneyPieChartIcon,
    ProfileIcon,
    RouteIcon,
    ShieldCheckIcon,
    TeamIcon
} from "@solomon-labs/design/components/icons";

const CLIENT_CONTROLLED_POSITION_NAV_ITEMS: NavEntry[] = [
    ...(FEATURE_FLAGS.portfolio
        ? [
              {
                  label: "Portfolio",
                  path: "/",
                  icon: <MoneyPieChartIcon className="size-6" />
              }
          ]
        : []),
    {
        label: "Positions",
        path: "/positions",
        icon: <CoinIcon className="size-6" />
    },
    {
        label: "Rewards Destinations",
        path: "/reward-destinations",
        icon: <RouteIcon className="size-6" />
    }
];

const CLIENT_END_USER_ENROLLMENT_NAV_ITEMS: NavEntry[] = [
    {
        label: "Programs Dashboard",
        path: "/programs-dashboard",
        icon: <LayoutGridIcon className="size-6" />
    },
    {
        label: "Programs",
        path: "/programs",
        icon: <BullhornIcon className="size-6" />
    },
    {
        label: "Program Policies",
        path: "/policies",
        icon: <ShieldCheckIcon className="size-6" />
    },
    {
        label: "Users",
        path: "/accounts",
        icon: <ProfileIcon className="size-6" />
    }
];

const REPORTING_NAV_ITEMS: NavEntry[] = [
    {
        label: "Reporting",
        path: "/reporting",
        icon: <GraphIcon className="size-6" />
    }
];

const COMPLIANCE_NAV_ITEMS: NavEntry[] = [
    {
        label: "Compliance",
        path: "/compliance",
        icon: <ShieldCheckIcon className="size-6" />
    }
];

/**
 * The client console home — where `/` lands and where the header logo points.
 * Portfolio (`/`) is home only when its feature flag is on; otherwise
 * Positions is the effective landing. Single source of truth so the index
 * route and logo never drift apart.
 */
export const CLIENT_HOME_PATH = FEATURE_FLAGS.portfolio ? "/" : "/positions";

export const CLIENT_NAV_ITEMS: NavEntry[] = buildClientNavItems();

export interface ClientNavOptions {
    complianceRequirementCount?: number | null;
    /** The Distributions access level is live: show the Distributor section. */
    distributionsAccess?: boolean;
}

/**
 * The client console nav.
 *
 * The Recipient section and Reporting are every company recipient's. The
 * Distributor section (programs for its own end users) follows the
 * Distributions access level the Distributor grants at creation, read from
 * the selected operating context (`distributionsAccess`).
 */
export function buildClientNavItems(
    options: ClientNavOptions = {}
): NavEntry[] {
    const showCompliance =
        FEATURE_FLAGS.clientComplianceSurface &&
        (options.complianceRequirementCount ?? 0) > 0;

    // Two roles, labelled: above, the client as a Recipient of the
    // distributor's programs (its own positions); below, the client as a
    // Distributor to its own end users, when it has that access level.
    return [
        { type: "section", label: "Recipient" },
        ...CLIENT_CONTROLLED_POSITION_NAV_ITEMS,
        ...(options.distributionsAccess
            ? ([
                  { type: "divider" },
                  { type: "section", label: "Distributor" },
                  ...CLIENT_END_USER_ENROLLMENT_NAV_ITEMS
              ] satisfies NavEntry[])
            : []),
        // Reporting covers both roles, so it sits apart from either group.
        { type: "divider" },
        ...REPORTING_NAV_ITEMS,
        ...(showCompliance ? COMPLIANCE_NAV_ITEMS : [])
    ];
}

/**
 * Pinned to the sidebar bottom — renders below Collapse, above the user
 * profile block. Team-member admin lives here. Company Settings used to
 * sit here too (→ `/organization`); it's been folded into the user
 * profile's Settings page (`/settings`, reached via the user's name), so
 * there's a single settings destination and no separate nav entry.
 *
 * API keys are an Owner's, and follow the API Access level the Distributor
 * grants (`apiAccess` on the selected operating context).
 */
export function buildClientBottomNavItems(
    isOwner: boolean,
    options: { apiAccess?: boolean } = {}
): NavEntry[] {
    const showApiKeys = isOwner && (options.apiAccess ?? true);
    return [
        ...(showApiKeys
            ? [
                  {
                      label: "API keys",
                      path: "/api-keys",
                      icon: <ShieldCheckIcon className="size-6" />
                  }
              ]
            : []),
        { label: "Team", path: "/team", icon: <TeamIcon className="size-6" /> }
    ];
}

export const CLIENT_BOTTOM_NAV_ITEMS: NavEntry[] =
    buildClientBottomNavItems(false);
